import React, { useState, useEffect } from 'react';
import './App.css';
import { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { Toaster, toast } from 'sonner';

registerLocale('de', de);

import { AuthProvider, useAuth } from './context/AuthContext';
import { registerServiceWorker, PWAInstallButton, OfflineBanner } from './components/pwa';
import LoginForm from './components/LoginForm';
import SsoPage from './components/SsoPage';
import PasswordSetupPage from './components/PasswordSetupPage';
import Dashboard from './components/Dashboard';
import BackofficeDashboard from './components/BackofficeDashboard';
import AdminPanel from './components/admin/AdminPanel';
import TeamleiterDashboardWithTabs from './components/TeamleiterDashboardWithTabs';

const App = () => {
  const { user, loading } = useAuth();
  
  const [, forceUpdate] = useState(0);

  // Handle Google Calendar OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gcal_connected') === 'true') {
      toast.success('Google Calendar erfolgreich verbunden!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('gcal_error')) {
      toast.error(`Google Calendar Fehler: ${params.get('gcal_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Register service worker on app load + handle bfcache
  useEffect(() => {
    registerServiceWorker();

    // Force re-render when returning from bfcache (mobile back button)
    const handlePageShow = (event) => {
      if (event.persisted) {
        forceUpdate(n => n + 1);
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);
  
  // Check for password setup route
  const currentPath = window.location.pathname;
  const isPasswordSetupRoute = currentPath.startsWith('/passwort-erstellen/');
  const isSsoRoute = currentPath === '/sso';

  // SSO route – Einmal-Login über CRM-Token
  if (isSsoRoute) {
    return <SsoPage />;
  }

  // If it's a password setup route, show the setup component
  if (isPasswordSetupRoute) {
    const token = currentPath.split('/')[2];
    return <PasswordSetupPage token={token} />;
  }

  const renderDashboard = () => {
    console.log('🔍 Rendering dashboard, user:', user); // Debug log
    if (!user) {
      console.log('❌ No user found, showing login form'); // Debug log
      return <LoginForm />;
    }
    
    switch (user.role) {
      case 'admin':
        return <AdminPanel />;
      case 'backoffice':
        return <BackofficeDashboard />;
      case 'vertriebsleiter':
      case 'teamleiter':
        return <TeamleiterDashboardWithTabs />;
      case 'vertriebler':
      default:
        return <Dashboard />;
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <OfflineBanner />
      {renderDashboard()}
      <PWAInstallButton />
    </div>
  );
};


const AppWithAuth = () => (
  <AuthProvider>
    <Toaster position="top-center" richColors closeButton />
    <App />
  </AuthProvider>
);

export default AppWithAuth;
